import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Network } from 'lucide-react';

import { KGViewer } from '@/components/KGViewer/KGViewer';
import { kgApi } from '@/lib/api';

interface KGContextPanelProps {
  highlightedSubject?: string | undefined;
  hops?: 1 | 2;
}

/**
 * KG context panel embedded in the Error Queue review layouts.
 *
 * Wraps `KGViewer` with the `/api/v1/kg/subgraph` fetch + lifecycle UI so the
 * layouts stay declarative. Mounts only when `highlightedSubject` is set.
 */
export function KGContextPanel({ highlightedSubject, hops = 1 }: KGContextPanelProps) {
  const enabled = Boolean(highlightedSubject);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['kg', 'subgraph', highlightedSubject, hops],
    queryFn: () => kgApi.subgraph(highlightedSubject as string, hops),
    enabled,
    staleTime: 60_000,
  });

  if (!enabled) {
    return (
      <EmptyState
        title="No subject in payload"
        body="This error has no subject IRI; the graph context cannot be rendered."
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full min-h-[20rem] items-center justify-center bg-slate-50 text-slate-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
        <span className="text-small">Loading subgraph…</span>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex h-full min-h-[20rem] flex-col items-center justify-center bg-amber-50 text-amber-700">
        <AlertTriangle className="mb-2 h-8 w-8" aria-hidden />
        <p className="text-small">Subgraph fetch failed</p>
        <p className="mt-1 font-mono text-small text-amber-600">{highlightedSubject}</p>
      </div>
    );
  }

  if (data.nodes.length === 0 || (data.nodes.length === 1 && data.edges.length === 0)) {
    return (
      <EmptyState
        title="Entity not yet in graph"
        body="No committed triples reference this subject. It may still be in the staging graph for the current batch."
        iri={highlightedSubject}
      />
    );
  }

  return (
    <div className="h-full min-h-[20rem] w-full">
      <KGViewer
        nodes={data.nodes}
        edges={data.edges}
        highlightedNodeIds={[data.root]}
      />
    </div>
  );
}

function EmptyState({
  title,
  body,
  iri,
}: {
  title: string;
  body: string;
  iri?: string | undefined;
}) {
  return (
    <div className="flex h-full min-h-[20rem] flex-col items-center justify-center bg-slate-50 px-4 text-center text-slate-500">
      <Network className="mb-2 h-8 w-8 text-slate-400" aria-hidden />
      <p className="text-small font-medium text-slate-600">{title}</p>
      <p className="mt-1 max-w-xs text-small text-slate-500">{body}</p>
      {iri && <p className="mt-2 break-all font-mono text-small text-slate-400">{iri}</p>}
    </div>
  );
}
